<template>
  <div id="analytics-dashboard">
    <div class="top-container">
      <section class="filter-result">
        <span>{{ result }}</span>
      </section>
      <CollapseController
        :isCollapsed="!isPresentingExpandedFilters"
        :showCalendar="false"
        @toggleCollapse="toggleExpandFilters"
      />
    </div>
    <div class="horizontal-divider"></div>
    <div class="scrollable-container" :class="computedClass">
      <DashboardBuildingScoresFilters
        :information="information.data.value"
        :isCollapsed="!isPresentingExpandedFilters"
        @formSubmitted="isPresentingExpandedFilters = false"
      />
      <UnderlineTabGroup
        :buttons="buttons"
        :size="'small'"
        :style="'margin-top: 16px;'"
        :selected-name="route.path.split('/').pop()"
      />
      <FeatureComponent feature="home/analytics/portfolio/buildingscore/*" />
    </div>
  </div>
</template>
<script lang="ts" setup>
import UnderlineTabGroup from '@/design-system/molecules/UnderlineTabGroup.vue'
import DashboardBuildingScoresFilters from './DashboardBuildingScoresFilters.vue'
import CollapseController from '@/components/AnalyticsDashboard/components/CollapseController/CollapseController.vue'
import { InformationQuery } from '@/api-client/models/buildingScore/Contracts'
import { watch, inject, computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { sharedDataKey } from '../../types'
import useFeatures from '@/pinia/features'
import nonEmptyList from '@/utils/nonEmptyList'
import isDefined from '@/utils/isDefined'
import { useResultReport } from '../useResultReport'
import useFetchInformation from '@/api-client/models/buildingScore/useFetchInformation'
import { useI18n } from 'vue-i18n'
import FeatureComponent from '@/components/FeatureComponent.vue'
import { FeatureType, useFilters } from '@/composables/useFilters'
import { useStore } from 'vuex'
import { useTransferState } from '../useTransferState'
type Emits = {
  (e: 'toggleFullscreen'): void
  (e: 'closeDashboard'): void
}
defineEmits<Emits>()

const { t } = useI18n()
const filters = useFilters(FeatureType.BuildingScore)
const features = useFeatures()
const { pushFeature, replaceFeature } = features
const route = useRoute()

const sharedData = inject(sharedDataKey)

const buttons = [
  {
    text: t('dashboard__text__measurement_values'),
    name: 'kpis',
    onClick: () => {
      replaceFeature('kpis')
    }
  },
  {
    text: t('dashboard__text__chart-graphs'),
    name: 'graphs',
    onClick: () => {
      replaceFeature('graphs')
    }
  }
]
const informationQuery = computed<InformationQuery>(() => {
  return {
    complexes: nonEmptyList([filters.submitted.complex?.belongsToId].filter(isDefined)),
    year: filters.submitted.year,
    constructions: nonEmptyList(filters.submitted.buildings.map((x) => x.id))
  }
})
const submittedInformationQuery = computed<InformationQuery>(() => {
  return {
    complexes: nonEmptyList([filters.submitted?.complex?.belongsToId].filter(isDefined)),
    year: filters.submitted?.year,
    constructions: nonEmptyList(filters.submitted?.buildings.map((x) => x.id))
  }
})
const information = useFetchInformation(informationQuery)
const submittedInformation = useFetchInformation(submittedInformationQuery)
const { result } = useResultReport(
  'buildingScore',
  submittedInformation.data,
  submittedInformation.isLoading
)
const store = useStore()
useTransferState(store)

const isPresentingExpandedFilters = ref(false)
const toggleExpandFilters = () => {
  isPresentingExpandedFilters.value = !isPresentingExpandedFilters.value
}

watch(
  () => route.path,
  (path) => {
    if (path.endsWith('buildingscore')) {
      pushFeature('kpis')
    }
  },
  { immediate: true }
)

onMounted(() => {
  information.fetch()
  sharedData?.updateSectionIndex(0)
})

const computedClass = computed(() => {
  return {
    fullscreen: sharedData?.fullscreen
  }
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

#analytics-dashboard {
  position: relative;
  width: 100%;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
  background-color: #fff;
  border-radius: variables.$border-radius-small;
  &.fullscreen {
    position: fixed;
    width: 100%;
    top: calc(#{variables.$layout-top-bar-height});
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 0;
    background-color: #fff;
  }
}
.top-container {
  flex-shrink: 0;
  padding: 0 24px 24px 24px;
  padding-bottom: 0;
  .dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    h4 {
      @include font-mixins.desktop-bold-h4;
    }
    button {
      border: none;
      border-radius: variables.$border-radius-small;
      display: flex;
      justify-content: center;
      align-items: center;
      background: none;
      padding: variables.$padding-medium;
      &:hover {
        background-color: rgba(0, 0, 0, 0.1);
      }
    }
  }
}
.scrollable-container {
  padding: 16px variables.$padding-main-body 24px variables.$padding-main-body;
  padding-top: 0px;
  overflow-y: scroll;
  scroll-behavior: smooth;
  scrollbar-width: none;
  background-color: #f9f9f9;
  height: 100%;
  &::-webkit-scrollbar {
    display: none;
  }
}
.horizontal-divider {
  height: 1px;
  width: 100%;
  background-color: #e0e0e0;
}

.filter-result {
  margin-top: 16px;
  span {
    @include font-mixins.desktop-regular-label;
    color: variables.$color-greyscale-content;
    display: flex;
    flex-direction: row;
    gap: variables.$gap-xlarge;
  }
}
</style>
